import React from 'react'
import { Search, FileText, Zap, HelpCircle, TrendingUp, Target, Users, BarChart3 } from 'lucide-react'
import { useTheme } from '@/context/ThemeContext'

type ChipDef = { icon: React.ElementType; label: string; prompt: string; roles: string[] }

const ALL_CHIPS: ChipDef[] = [
  { icon: Search, label: 'Find leads', prompt: 'Find leads matching my ICP', roles: ['owner', 'admin', 'member'] },
  { icon: FileText, label: 'Draft email', prompt: 'Draft an outreach email', roles: ['owner', 'admin', 'member'] },
  { icon: Zap, label: 'Campaign stats', prompt: 'Show my campaign performance', roles: ['owner', 'admin', 'member'] },
  { icon: HelpCircle, label: 'Research prospect', prompt: 'Research a prospect or company', roles: ['owner', 'admin', 'member'] },
  { icon: Target, label: 'Summarize pipeline', prompt: 'Summarize my current pipeline', roles: ['owner', 'admin', 'member'] },
  { icon: TrendingUp, label: 'Stalled deals', prompt: 'Show deals stalled for more than 30 days', roles: ['owner', 'admin'] },
  { icon: BarChart3, label: 'Export forecast', prompt: 'Generate a quarterly forecast report', roles: ['owner', 'admin'] },
  { icon: Users, label: 'Team performance', prompt: 'Show team performance overview', roles: ['owner', 'admin'] },
]

interface QuickActionChipsProps {
  userRole?: string
  onChipClick: (prompt: string) => void
}

export const QuickActionChips: React.FC<QuickActionChipsProps> = ({ userRole = 'member', onChipClick }) => {
  const { currentTheme } = useTheme()
  const chips = ALL_CHIPS.filter(c => c.roles.includes(userRole))

  if (chips.length === 0) return null

  return (
    <div
      className="flex gap-2 overflow-x-auto px-4 pb-2 pt-1 max-w-[60rem] mx-auto custom-scrollbar"
      style={{ scrollbarWidth: 'none' }}
    >
      {chips.map(({ icon: Icon, label, prompt }) => (
        <button
          key={label}
          onClick={() => onChipClick(prompt)}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap shrink-0 transition-all duration-200"
          style={{
            backgroundColor: currentTheme.secondary,
            border: `1px solid ${currentTheme.border}`,
            color: currentTheme.text,
          }}
          onMouseEnter={e => {
            e.currentTarget.style.borderColor = currentTheme.accent
            e.currentTarget.style.backgroundColor = currentTheme.hoverBackground
          }}
          onMouseLeave={e => {
            e.currentTarget.style.borderColor = currentTheme.border
            e.currentTarget.style.backgroundColor = currentTheme.secondary
          }}
        >
          <Icon className="w-3 h-3" style={{ color: currentTheme.accent }} />
          {label}
        </button>
      ))}
    </div>
  )
}
